import { createStore } from 'vuex'
import axios from 'axios'
import moment from 'moment'
import i18n from '@/utils/i18n'
moment.locale('it')
i18n.global.locale.value = 'it'

// -----------------------------------------------------------------------------
// Image fetch queue (private S3 -> backend proxy).
// This reduces backend load spikes and makes UI updates less "random".
// If you want strict top-to-bottom ordering, set VUE_APP_IMAGE_FETCH_CONCURRENCY=1.
// -----------------------------------------------------------------------------
const IMAGE_FETCH_CONCURRENCY = Number(process.env.VUE_APP_IMAGE_FETCH_CONCURRENCY || 4)
let _activeImageFetches = 0
const _imageFetchQueue = []
const _inFlightImageFetches = new Map() // url -> Promise<AxiosResponse<Blob>>

function _pumpImageFetchQueue () {
  while (_activeImageFetches < IMAGE_FETCH_CONCURRENCY && _imageFetchQueue.length > 0) {
    const job = _imageFetchQueue.shift()
    _activeImageFetches++
    Promise.resolve()
      .then(job.task)
      .then(job.resolve, job.reject)
      .finally(() => {
        _activeImageFetches--
        _pumpImageFetchQueue()
      })
  }
}

function _enqueueImageFetch (task) {
  return new Promise((resolve, reject) => {
    _imageFetchQueue.push({ task, resolve, reject })
    _pumpImageFetchQueue()
  })
}

// ---INITIAL STATE
const getDefaultState = () => {
  return {
    user: null,
    mail: null
  }
}

let loaded = false

const store = createStore({
  state: getDefaultState(),

  // ---GETTERS
  getters: {
    isAuthenticated: state => !!state.user,
    stateUser: state => state.user,
    stateMail: state => state.mail
  },

  // ---MUTATIONS
  mutations: {
    resetState (state) {
      Object.assign(state, getDefaultState())
    },
    setUser (state, user) {
      state.user = user
      if (user) localStorage.setItem(process.env.VUE_APP_NAME + 'store', JSON.stringify(user))
      else localStorage.removeItem(process.env.VUE_APP_NAME + 'store')
    },
    setUserMail (state, mail) {
      state.mail = mail
      if (mail) localStorage.setItem(process.env.VUE_APP_NAME + 'currentMail', mail)
      else localStorage.removeItem(process.env.VUE_APP_NAME + 'currentMail')
    },
    initializeStore (state) {
      if (!loaded) {
        if (localStorage.getItem(process.env.VUE_APP_NAME + 'store')) {
          state.user = JSON.parse(localStorage.getItem(process.env.VUE_APP_NAME + 'store'))
          state.mail = localStorage.getItem(process.env.VUE_APP_NAME + 'currentMail')
        }
        loaded = true
      }
    }
  },

  // ---ACTIONS
  actions: {
    resetStore ({ commit }) {
      commit('resetState')
    },
    // --Get logs
    async getHistory ({ state }, { bucket, phone, desc, offset, limit, categories }) {
      const data = state.user
      const off = 0 // offset || 0
      const lim = 100 // limit || 10000
      let rsp
      if (data) {
        const Authorization = data.token_type + ' ' + data.access_token
        let query = '/logs/?skip=' + off + '&limit=' + lim
        if (bucket) query = query + '&bucket=' + bucket
        if (phone) query = query + '&extra_data=' + phone
        if (desc) query = query + '&description=' + desc
        rsp = await axios.get(query, {
          headers: {
            Authorization
          }
        })
          .then((rsp) => {
            let logs = rsp.data
            // console.log(logs)
            // -Filter by PHONE
            if (phone) {
              logs = logs.filter((el) => {
                const currentNumber = el.extra_data.phone_number
                const thirtynined = '+39' + phone
                const thirtyninedCurrent = '+39' + currentNumber
                return currentNumber === phone || currentNumber === thirtynined || phone === thirtyninedCurrent
              })
            }

            if (desc) {
              logs = logs.filter((el) => {
                const currentDesc = el.description
                return currentDesc.includes(desc)
              })
            }

            for (let i = 0; i < logs.length; i++) {
              // const offset = moment().utcOffset()
              // let temp
              // if (logs[i].extra_data.timestamp) {
              //   temp = moment.parseZone(logs[i].extra_data.timestamp).utcOffset(offset).format('lll')
              // } else {
              //   temp = moment.parseZone(logs[i].created_at).utcOffset(offset).format('lll')
              // }
              if (logs[i].extra_data.timestamp) {
                logs[i].created_at = logs[i].extra_data.timestamp
              }
            }

            logs.sort((a, b) => {
              return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            })

            for (let i = 0; i < logs.length; i++) {
              const offset = moment().utcOffset()
              const temp = moment.parseZone(logs[i].created_at).utcOffset(offset).format('lll')
              logs[i].created_at = temp
            }

            if (categories && categories.length) {
              logs = logs.filter((el, id) => {
                return categories.includes(el.category)
              })
            }

            const returnedValues = []
            for (let i = offset; i < limit; i++) {
              if (logs[i]) returnedValues.push(logs[i])
            }
            return returnedValues
          })
          .catch((e) => {
            console.error('HISTORY', e)
            return false
          })
      } else {
        console.error('NOT AUTHENTICATED', store.user)
        rsp = false
      }
      return rsp
    },
    // --Send SMS
    async sendSMS ({ state }, payload) {
      const data = state.user
      const Authorization = data.token_type + ' ' + data.access_token
      const rsp = await axios.post('/utils/send-sms/', payload, {
        headers: {
          Authorization
        }
      })
        .then((rsp) => {
          // console.log('---SEND MAIL', rsp)
          return rsp
        })
        .catch((e) => {
          console.error('SEND MAIL', e)
          return false
        })
      return rsp
    },
    // --Send Mail Report
    async sendMail ({ state }, payload) {
      const data = state.user
      const Authorization = data.token_type + ' ' + data.access_token
      const rsp = await axios.post('/utils/export-partecipations/', payload, {
        headers: {
          Authorization
        }
      })
        .then((rsp) => {
          // console.log('---SEND MAIL', rsp)
          return rsp
        })
        .catch((e) => {
          console.error('SEND MAIL', e)
          return false
        })
      return rsp
    },
    // --Send Mail Report (WINNERS)
    async sendMailWinners ({ state }, payload) {
      const data = state.user
      const Authorization = data.token_type + ' ' + data.access_token
      const rsp = await axios.post('/utils/export-win/', payload, {
        headers: {
          Authorization
        }
      })
        .then((rsp) => {
          // console.log('---SEND MAIL', rsp)
          return rsp
        })
        .catch((e) => {
          console.error('SEND MAIL', e)
          return false
        })
      return rsp
    },
    // --Send Mail Report (not WINNERS)
    async sendMailNotWinners ({ state }, payload) {
      const data = state.user
      const Authorization = data.token_type + ' ' + data.access_token
      const rsp = await axios.post('/utils/export-not-winners/', payload, {
        headers: {
          Authorization
        }
      })
        .then((rsp) => {
          // console.log('---SEND MAIL', rsp)
          return rsp
        })
        .catch((e) => {
          console.error('SEND MAIL', e)
          return false
        })
      return rsp
    },
    getOuterImage ({ state }, url) {
      const data = state.user
      if (data) {
        const Authorization = data.token_type + ' ' + data.access_token
        const key = String(url)
        if (_inFlightImageFetches.has(key)) return _inFlightImageFetches.get(key)

        const promise = _enqueueImageFetch(() => {
          return axios.get('/dashboard/image/?url=' + encodeURIComponent(url), {
            responseType: 'blob',
            headers: { Authorization }
          })
        })
          .catch((err) => {
            throw fireError(err)
          })
          .finally(() => {
            _inFlightImageFetches.delete(key)
          })

        _inFlightImageFetches.set(key, promise)
        return promise
      }
    },
    // --Campaign Stats
    async getStats ({ commit, state }) {
      const data = state.user
      if (data) {
        const Authorization = data.token_type + ' ' + data.access_token
        return await axios.get('/utils/stats', {
          headers: {
            Authorization
          }
        })
          .then((rsp) => {
            return rsp.data
          })
          .catch(async (e) => {
            console.error('STATS', e)
            commit('setUser', null)
            commit('setUserMail', null)
            return false
          })
      }
    },
    // --UPDATE release
    async updateRelease ({ state }, pay) {
      const data = state.user
      const Authorization = data.token_type + ' ' + data.access_token
      const rsp = await axios.put('/release/evaluate-release/' + pay.id, pay.pay, {
        headers: {
          Authorization
        }
      })
        .then((rsp) => {
          return rsp
        })
        .catch((e) => {
          console.error('UPDATE RELEASE', e)
          return false
        })
      return rsp
    },
    // --Confirm release
    async confirmRelease ({ state }, payload) {
      const data = state.user
      if (data) {
        const Authorization = data.token_type + ' ' + data.access_token
        return await axios.post('/items/release/' + payload.id, payload.data, {
          headers: {
            Authorization
          }
        })
          .then((rsp) => {
            console.log('OK DONE', rsp)
            return rsp.data
          })
          .catch((e) => {
            console.log('NOT DONE', e)
            return fireError(e)
          })
      }
    },
    // --Export URA
    async exportURA ({ state }, payload) {
      const data = state.user
      if (data) {
        const Authorization = data.token_type + ' ' + data.access_token
        return await axios.post('/utils/export-ura/', payload, {
          headers: {
            Authorization
          }
        })
          .then((rsp) => {
            // console.log('OK DONE', rsp)
            return rsp.data
          })
          .catch((e) => {
            // console.log('NOT DONE', e)
            return fireError(e)
          })
      }
    },
    // --Get all user items
    async getItems ({ commit, state }, payload) {
      const data = state.user
      if (data) {
        const Authorization = data.token_type + ' ' + data.access_token
        const query = '/items/items-to-approve?skip=' + payload.off + '&limit=' + payload.lim
        return await axios.get(query, {
          headers: {
            Authorization
          }
        })
          .then((rsp) => {
            return rsp.data
          })
          .catch(async (e) => {
            console.error('GET ITEMS', e)
            commit('setUser', null)
            commit('setUserMail', null)
            return false
          })
      }
    },
    // --Get all user releases
    async getReleases ({ commit, state }, payload) {
      const data = state.user
      if (data) {
        const Authorization = data.token_type + ' ' + data.access_token
        const query = '/release/releases-to-approve?skip=' + payload.off + '&limit=' + payload.lim
        return await axios.get(query, {
          headers: {
            Authorization
          }
        })
          .then((rsp) => {
            return rsp.data
          })
          .catch(async (e) => {
            console.error('GET RELEASES', e)
            commit('setUser', null)
            commit('setUserMail', null)
            return false
          })
      }
    },
    // --Login
    async logIn ({ commit }, data) {
      // console.log('LOGIN DATA', data.get('username'))
      const rsp = await axios.post('/login/access-token', data)
        .then((rsp) => {
          commit('setUser', rsp.data)
          commit('setUserMail', data.get('username'))
          return rsp.data
        })
        .catch((e) => {
          console.error('LOGIN', e)
          return false
        })
      return rsp
    },
    // --Registration confirm
    async confirmUser ({ state }, payload) {
      const data = state.user
      const Authorization = data.token_type + ' ' + data.access_token
      const rsp = await axios.post('/users/activate', payload, {
        headers: {
          Authorization
        }
      })
        .then((rsp) => {
          return rsp
        })
        .catch((e) => {
          console.error('CONFIRM USER', e)
          return false
        })
      return rsp
    },
    // --Recover password
    async recoverPassword ({ state }, payload) {
      const data = state.user
      const Authorization = data.token_type + ' ' + data.access_token
      const rsp = await axios.post('/password-recovery/' + payload.email, {
        headers: {
          Authorization
        }
      })
        .then((rsp) => {
          return rsp
        })
        .catch((e) => {
          console.error('RESET PASSWORD', e)
          return false
        })
      return rsp
    },
    // --Reset password
    async resetPassword ({ state }, payload) {
      const data = state.user
      const Authorization = data.token_type + ' ' + data.access_token
      const rsp = await axios.post('/reset-password', payload, {
        headers: {
          Authorization
        }
      })
        .then((rsp) => {
          return rsp
        })
        .catch((e) => {
          console.error('RESET PASSWORD', e)
          return false
        })
      return rsp
    },
    // --Logout
    logout ({ commit }) {
      commit('setUser', null)
      commit('setUserMail', null)
    }
  },

  // ---MODULES
  modules: {}
})

store.subscribe((mutation, state) => {
  if (state.user) localStorage.setItem(process.env.VUE_APP_NAME + 'store', JSON.stringify(state.user))
  else localStorage.removeItem(process.env.VUE_APP_NAME + 'store')

  if (state.mail) localStorage.setItem(process.env.VUE_APP_NAME + 'currentMail', state.mail)
  else localStorage.removeItem(process.env.VUE_APP_NAME + 'currentMail')
})

function fireError (err) { // ---ERROR MANAGER
  // Sentry.captureException(err)
  const detail = err?.response?.data?.detail
  if (detail === undefined) {
    return {
      detail: [err],
      msg_translated: 'Errore generico'
    }
  }
  if (detail === 'api.disabled') {
    return {
      detail: [err],
      msg_translated: 'Concorso NON attivo'
    }
  }
  const error = err.response.data.detail.reduce((a, v) => {
    if (v.msg.startsWith('Email not valid The domain name')) {
      return a + 'Email non valida: dominio inesistente.<br/>'
    } else {
      return a + i18n.global.t(v.msg) + '<br/>'
    }
  }, '')
  err.response.data.msg_translated = error
  return err.response.data
}

export default store
